import { HeatAgeThresholdDraft } from "@/shared/models/HeatAgeThreshold";
import {
	HEAT_AGE_COLORS,
	HEAT_AGE_OK_COLORS,
} from "@/shared/utils/heatAgeLevels";
import { Box, Typography } from "@mui/material";
import { useRef, useState } from "react";

const STEP_DAYS = 1;

type Props = {
	levels: HeatAgeThresholdDraft[];
	maxDays: number;
	disabled?: boolean;
	onChangeDays: (index: number, days: number) => void;
};

export const HeatAgeThresholdBar = ({
	levels,
	maxDays,
	disabled = false,
	onChangeDays,
}: Props) => {
	const barRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<number | null>(null);

	const toDays = (clientX: number) => {
		if (!barRef.current) return 0;
		const rect = barRef.current.getBoundingClientRect();
		const ratio = (clientX - rect.left) / rect.width;
		const days = Math.max(0, Math.min(1, ratio)) * maxDays;
		return Math.round(days / STEP_DAYS) * STEP_DAYS;
	};

	const clamp = (index: number, days: number) => {
		const min = index * STEP_DAYS;
		const max = maxDays - (levels.length - 1 - index) * STEP_DAYS;
		return Math.max(min, Math.min(max, days));
	};

	const handleMouseDown = (index: number) => (e: React.MouseEvent) => {
		if (disabled) return;
		e.preventDefault();
		setDragging(index);

		const handleMouseMove = (moveEvent: MouseEvent) =>
			onChangeDays(index, clamp(index, toDays(moveEvent.clientX)));

		const handleMouseUp = () => {
			setDragging(null);
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
	};

	const segments = [
		{ from: 0, to: levels[0]?.Days ?? maxDays, ...HEAT_AGE_OK_COLORS },
		...levels.map((level, index) => ({
			from: level.Days,
			to: index < levels.length - 1 ? levels[index + 1].Days : maxDays,
			...HEAT_AGE_COLORS[level.Level],
		})),
	];

	return (
		<Box>
			<Box
				ref={barRef}
				sx={{
					position: "relative",
					height: 40,
					borderRadius: 1,
					border: "1px solid #ddd",
					display: "flex",
					userSelect: "none",
				}}
			>
				{segments.map((segment) => {
					const width = Math.max(
						((segment.to - segment.from) / maxDays) * 100,
						0,
					);
					if (width === 0) return null;

					return (
						<Box
							key={`${segment.from}-${segment.backgroundColor}`}
							sx={{
								width: `${width}%`,
								backgroundColor: segment.backgroundColor,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								transition: dragging !== null ? "none" : "width 0.2s ease",
							}}
						>
							<Typography
								sx={{
									fontSize: "0.7rem",
									fontWeight: 600,
									color: segment.color,
									whiteSpace: "nowrap",
									pointerEvents: "none",
								}}
							>
								{segment.from}d -{" "}
								{segment.to >= maxDays ? " or more" : `${segment.to}d`}
							</Typography>
						</Box>
					);
				})}

				{levels.map((level, index) => (
					<Box
						key={level.Level}
						onMouseDown={handleMouseDown(index)}
						sx={{
							position: "absolute",
							left: `${(level.Days / maxDays) * 100}%`,
							top: -4,
							bottom: -4,
							width: 14,
							marginLeft: "-7px",
							cursor: disabled ? "default" : "ew-resize",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: dragging === index ? 10 : 5,
							"&:hover > div, &:active > div": {
								transform: disabled ? "none" : "scale(1.3)",
							},
						}}
					>
						<Box
							sx={{
								width: 8,
								height: 46,
								borderRadius: 1,
								backgroundColor: dragging === index ? "#333" : "#666",
								border: "1px solid #fff",
								boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
								transition: "transform 0.1s, background-color 0.1s",
							}}
						/>
					</Box>
				))}
			</Box>

			<Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.25 }}>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>0d</Typography>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>
					{maxDays}d+
				</Typography>
			</Box>
		</Box>
	);
};
